<app-section-card title="Scene Settings" [collapsed]="false">
	<div class="px-3 py-2.5 flex flex-col gap-2">
		<div class="form-row">
			<div class="form-field">
				<label class="form-label">Slug</label>
				<input
					pInputText
					class="w-full"
					[value]="data().slug ?? ''"
					(change)="onSlugChange($any($event.target).value)"
				/>
			</div>
			<div class="form-field">
				<label class="form-label">Name</label>
				<input pInputText class="w-full" [value]="data().name ?? ''" (change)="setField('name', $any($event.target).value)" />
			</div>
		</div>
		<div class="form-row">
			<div class="form-field">
				<label class="form-label">Frames Count</label>
				<p-inputnumber
					[ngModel]="data().framesCount ?? 0"
					(ngModelChange)="setFramesCount($event)"
					styleClass="p-inputnumber-fluid"
					[showButtons]="false"
				/>
			</div>
			<div class="form-field">
				<label class="form-label">Playback Type</label>
				<p-select
					[options]="playbackTypeOptions"
					optionValue="value"
					appendTo="body"
					[ngModel]="data().playbackType ?? ''"
					(ngModelChange)="setField('playbackType', $event)"
					styleClass="p-select-fluid"
				/>
			</div>
		</div>
		@if (data().background?.source?.dataType === 'IMAGE') {
			<div class="form-row">
				<div class="form-field">
					<label class="form-label">Duration (s)</label>
					<p-inputnumber
						data-scene-duration-input
						[ngModel]="durationSeconds()"
						(ngModelChange)="setDurationSeconds($event)"
						[minFractionDigits]="0"
						[maxFractionDigits]="2"
						[min]="0"
						styleClass="p-inputnumber-fluid"
						[showButtons]="false"
					/>
				</div>
			</div>
		}
		<div class="form-row">
			<div class="form-field form-field--check">
				<p-checkbox inputId="scene-disable-pause-check" [binary]="true" [ngModel]="data().disablePause ?? false" (ngModelChange)="setBool('disablePause', $event)" />
				<label class="form-label" for="scene-disable-pause-check">Disable Pause</label>
			</div>
			<div class="form-field form-field--check">
				<p-checkbox inputId="scene-disable-osd-check" [binary]="true" [ngModel]="data().disabledOsdTimeline ?? false" (ngModelChange)="setBool('disabledOsdTimeline', $event)" />
				<label class="form-label" for="scene-disable-osd-check">Disable OSD Timeline</label>
			</div>
			<div class="form-field form-field--check">
				<p-checkbox inputId="scene-alt-check" [binary]="true" [ngModel]="sceneRefAlt()" (ngModelChange)="sceneRefAltChange.emit($event)" />
				<label class="form-label" for="scene-alt-check">Alternative scene</label>
			</div>
		</div>
		<div class="wb-divider-top">
			<label class="form-label">Attributes</label>
			<app-attributes-editor
				[data]="sceneRefAttributes()"
				(dataChange)="sceneRefAttributesChange.emit($event)"
			/>
		</div>
		@if (sceneRefVariant()) {
			<app-variant-editor
				[data]="sceneRefVariant()"
				mode="scene"
				[argKeys]="argKeys()"
				[siblingSlug]="siblingSlug()"
				(dataChange)="sceneRefVariantChange.emit($event)"
			/>
		}
	</div>
</app-section-card>

<app-section-card title="Background" [collapsed]="false">
	<div class="px-3 py-2.5 flex flex-col gap-2">
		<div class="form-row">
			<div class="form-field flex-1">
				<label class="form-label">Media</label>
				<div class="picker-row">
					<input
						pInputText
						class="flex-1 text-xs"
						readonly
						[value]="fileName(data().background?.source?.src)"
						[title]="data().background?.source?.src ?? ''"
					/>
					<button class="picker-btn" type="button" (click)="openBgPicker()" title="Browse files">
						<i class="pi pi-folder-open text-xs"></i>
					</button>
					@if (data().background?.source?.src) {
						<button class="picker-btn" type="button" (click)="clearBgSrc()" title="Clear media">
							<i class="pi pi-times text-xs"></i>
						</button>
					}
					@if (canGenerate() && !generateStatus()) {
						<button class="picker-btn" type="button" (click)="startGenerate()" [title]="hasVersions() ? 'Re-generate versions' : 'Generate versions'">
							@if (hasVersions()) {
								<i class="pi pi-check text-xs" style="color: var(--wb-success)"></i>
							} @else {
								<i class="pi pi-cog text-xs"></i>
							}
						</button>
					}
				</div>
				@if (generateStatus(); as job) {
					<div class="generate-status" [class.generate-status--error]="job.status === 'error'">
						@if (job.status === 'error') {
							<i class="pi pi-exclamation-triangle text-[10px]"></i>
							<span class="flex-1 truncate">{{ job.error ?? 'Failed' }}</span>
							<button class="generate-dismiss" type="button" (click)="dismissGenerateStatus()" title="Dismiss">
								<i class="pi pi-times text-[8px]"></i>
							</button>
						} @else {
							<i class="pi pi-spin pi-spinner text-[10px]"></i>
							<span class="flex-1 truncate">{{ job.currentStep }} ({{ job.completedVersions.length }}/{{ job.totalVersions || '?' }})</span>
						}
					</div>
				}
			</div>
		</div>
		<div class="form-row">
			<div class="form-field form-field">
				<label class="form-label">Type</label>
				<p-select
					[options]="bgTypeOptions"
					optionValue="value"
					appendTo="body"
					[ngModel]="data().background?.source?.dataType ?? 'VIDEO'"
					(ngModelChange)="setBgField('dataType', $event)"
					styleClass="p-select-fluid"
				/>
			</div>
			@if (data().background?.source?.dataType === 'VIDEO') {
				<div class="form-field form-field--check">
					<p-checkbox
						inputId="scene-bg-autoplay-check"
						[binary]="true"
						[ngModel]="data().background?.source?.autoPlay ?? false"
						(ngModelChange)="setBgField('autoPlay', $event)"
					/>
					<label class="form-label" for="scene-bg-autoplay-check">AutoPlay</label>
				</div>
				<div class="form-field form-field--check">
					<p-checkbox
						inputId="scene-bg-loop-check"
						[binary]="true"
						[ngModel]="data().background?.source?.loop ?? false"
						(ngModelChange)="setBgField('loop', $event)"
					/>
					<label class="form-label" for="scene-bg-loop-check">Loop</label>
				</div>
				<div class="form-field form-field--check">
					<p-checkbox
						inputId="scene-bg-mask-check"
						[binary]="true"
						[ngModel]="data().mask ?? false"
						(ngModelChange)="setMask($event)"
					/>
					<label class="form-label" for="scene-bg-mask-check">Mask</label>
				</div>
			}
		</div>
		@if (data().mask && data().background?.source?.dataType === 'VIDEO') {
			<div class="form-row">
				<div class="form-field flex-1">
					<label class="form-label">Mask Source</label>
					<div class="picker-row">
						<input
							pInputText
							class="flex-1"
							[value]="data().background?.source?.maskSrc ?? ''"
							(input)="setMaskSrc($any($event.target).value)"
						/>
						<button class="picker-btn" type="button" (click)="openMaskPicker()" title="Browse mask files">
							<i class="pi pi-folder-open text-xs"></i>
						</button>
						@if (canGenerateMask() && !generateMaskStatus()) {
							<button class="picker-btn" type="button" (click)="startGenerateMask()" [title]="hasMaskVersions() ? 'Re-generate mask' : 'Generate mask versions'">
								@if (hasMaskVersions()) {
									<i class="pi pi-check text-xs" style="color: var(--wb-success)"></i>
								} @else {
									<i class="pi pi-cog text-xs"></i>
								}
							</button>
						}
					</div>
					@if (generateMaskStatus(); as maskJob) {
						<div class="generate-status" [class.generate-status--error]="maskJob.status === 'error'">
							@if (maskJob.status === 'error') {
								<i class="pi pi-exclamation-triangle text-[10px]"></i>
								<span class="flex-1 truncate">{{ maskJob.error ?? 'Failed' }}</span>
								<button class="generate-dismiss" type="button" (click)="dismissGenerateMaskStatus()" title="Dismiss">
									<i class="pi pi-times text-[8px]"></i>
								</button>
							} @else {
								<i class="pi pi-spin pi-spinner text-[10px]"></i>
								<span class="flex-1 truncate">{{ maskJob.currentStep }} ({{ maskJob.completedVersions.length }}/{{ maskJob.totalVersions || '?' }})</span>
							}
						</div>
					}
				</div>
			</div>
			<!-- Video Mask composite button — requires both video + mask to be local -->
			@if (canComposite()) {
				<div class="form-row">
					<div class="form-field flex-1">
						<div class="flex gap-1 items-center">
							<button
								class="composite-btn"
								type="button"
								[disabled]="!compositeReady() || compositeStatus()?.status === 'running' || compositeStatus()?.status === 'pending'"
								(click)="startComposite()"
								[title]="compositeTooltip()"
							>
								<i class="pi pi-clone text-xs"></i>
								<span>Create Video Mask</span>
							</button>
						</div>
						@if (compositeStatus(); as compJob) {
							<div class="generate-status" [class.generate-status--error]="compJob.status === 'error'">
								@if (compJob.status === 'error') {
									<i class="pi pi-exclamation-triangle text-[10px]"></i>
									<span class="flex-1 truncate">{{ compJob.error ?? 'Failed' }}</span>
									<button class="generate-dismiss" type="button" (click)="dismissCompositeStatus()" title="Dismiss">
										<i class="pi pi-times text-[8px]"></i>
									</button>
								} @else {
									<i class="pi pi-spin pi-spinner text-[10px]"></i>
									<span class="flex-1 truncate">{{ compJob.currentStep }} ({{ compJob.completedVersions.length }}/{{ compJob.totalVersions || '?' }})</span>
								}
							</div>
						}
					</div>
				</div>
			}
		}
	</div>
</app-section-card>

<app-content-items-form
	[content]="data().content ?? []"
	[aeComp]="aeComp()"
	(contentChange)="onContentChange($event)"
	(copyItem)="copyItem.emit($event)"
/>

<!-- Actions -->
<div class="flex flex-col gap-1.5">
	<div class="flex items-center justify-between gap-3 px-3 py-2 bg-wb-surface rounded-wb-sm border border-wb-border">
		<span class="text-[13px] font-semibold text-wb-text flex items-center gap-1.5">
			Actions
			@if ((data().actions ?? []).length > 0) {
				<span class="wb-count-badge">{{ (data().actions ?? []).length }}</span>
			}
		</span>
		<div class="flex gap-1.5 items-center">
			<p-select
				[options]="actionTypeOptions"
				optionValue="value"
				appendTo="body"
				[ngModel]="newActionType()"
				(ngModelChange)="newActionType.set($event)"
			/>
			<button pButton size="small" (click)="addAction()"><i class="pi pi-plus"></i>Add</button>
		</div>
	</div>
	@if ((data().actions ?? []).length === 0) {
		<div class="text-center text-wb-muted text-[12px] py-4 px-3 bg-wb-bg border border-dashed border-wb-border rounded-wb-sm">No actions.</div>
	}
	@for (action of data().actions; track $index; let ai = $index) {
		<div class="action-card">
			<div class="action-header" (click)="toggleAction(ai)">
				<button
					pButton
					[text]="true"
					size="small"
					severity="secondary"
					class="action-chevron-btn"
					(click)="$event.stopPropagation(); toggleAction(ai)"
				>
					<i class="pi" [class.pi-chevron-down]="actionExpanded().has(ai)" [class.pi-chevron-right]="!actionExpanded().has(ai)"></i>
				</button>
				<span class="action-badge action-badge--{{ action.type ?? 'LINK' }}">{{ action.type ?? '?' }}</span>
				<span class="text-[12px] text-wb-text truncate flex-1">{{ action.name || action.slug || '(unnamed)' }}</span>
				<span class="action-actions" (click)="$event.stopPropagation()">
					<button pButton [text]="true" size="small" severity="secondary" [disabled]="ai === 0" (click)="moveAction(ai, -1)"><i class="pi pi-chevron-up"></i></button>
					<button pButton [text]="true" size="small" severity="secondary" [disabled]="ai === (data().actions?.length ?? 0) - 1" (click)="moveAction(ai, 1)"><i class="pi pi-chevron-down"></i></button>
					<button pButton [text]="true" size="small" severity="secondary" title="Copy to another scene" (click)="requestCopyAction(ai)"><i class="pi pi-copy"></i></button>
					<button pButton [text]="true" size="small" severity="danger" (click)="removeAction(ai)"><i class="pi pi-trash"></i></button>
				</span>
			</div>
				@if (actionExpanded().has(ai)) {
					<div class="action-body">
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Type</label>
								<p-select [options]="actionTypeOptions" optionValue="value" appendTo="body"
									[ngModel]="action.type ?? ''" (ngModelChange)="setActionField(ai, 'type', $event)"
									styleClass="p-select-fluid" />
							</div>
							<div class="form-field">
								<label class="form-label">Name</label>
								<input pInputText class="w-full text-xs" [value]="action.name ?? ''"
									(change)="setActionField(ai, 'name', $any($event.target).value)" />
							</div>
							<div class="form-field">
								<label class="form-label">Slug</label>
								<input pInputText class="w-full text-xs" [value]="action.slug ?? ''"
									(change)="setActionField(ai, 'slug', $any($event.target).value)" />
							</div>
						</div>
						<!-- Position mode toggle -->
						<div class="form-row">
							<div class="form-field flex-1">
								<label class="form-label">Position</label>
								<div class="flex items-center gap-1.5">
									<div class="mode-toggle">
										<button type="button" class="mode-btn" [class.mode-btn--active]="getActionMode(action) === 'static'"
											(click)="setActionMode(ai, 'static')">Static</button>
										<button type="button" class="mode-btn" [class.mode-btn--active]="getActionMode(action) === 'dynamic'"
											(click)="setActionMode(ai, 'dynamic')">Dynamic</button>
									</div>
									<button type="button" class="coord-action-btn"
										[class.coord-action-btn--active]="aeInputOpenForAi() === ai"
										(click)="aeInputOpenForAi() === ai ? closeAeInput() : openAeInput(ai)"
										title="Import from After Effects">
										<i class="pi pi-download"></i>
									</button>
									@if (getActionMode(action)) {
										<p-popover #coordsPop>
											<div class="ae-tip-scroll"
												[innerHTML]="getActionCoordsSummary(action)"
												(mouseenter)="cancelCoordsHide()"
												(mouseleave)="hideCoordsPopover(coordsPop)">
											</div>
										</p-popover>
										<button type="button" class="coord-action-btn"
											(click)="exportAeToClipboard(ai)"
											title="Copy as After Effects Corner Pin data">
											<i class="pi pi-upload"></i>
										</button>
										<button type="button" class="coord-action-btn"
											(mouseenter)="showCoordsPopover(coordsPop, $event)"
											(mouseleave)="hideCoordsPopover(coordsPop)"
											title="Show coordinates">
											<i class="pi pi-question-circle"></i>
										</button>
										<button type="button" class="coord-action-btn coord-action-btn--danger"
											(click)="clearActionCoords(ai)"
											title="Clear position">
											<i class="pi pi-times"></i>
										</button>
									}
									@if (aeImportStatus()?.ai === ai && aeInputOpenForAi() !== ai) {
										<span class="ae-status" [class.ae-status--error]="aeImportStatus()!.type === 'error'">
											<i class="pi"
												[class.pi-check-circle]="aeImportStatus()!.type === 'ok'"
												[class.pi-exclamation-triangle]="aeImportStatus()!.type === 'error'"></i>
											{{ aeImportStatus()!.message }}
										</span>
									}
								</div>
							</div>
						</div>
						<!-- AE keyframe import (shown when open) -->
						@if (aeInputOpenForAi() === ai) {
							<div class="form-row">
								<div class="form-field" style="flex-basis: 100%">
									<label class="form-label">Import from After Effects</label>
									<div class="ae-input-area">
										<textarea pTextarea class="ae-import-textarea w-full"
											[attr.data-ae-input]="ai"
											placeholder="Paste AE keyframe data here (Ctrl+V)..."
											rows="3"
											(paste)="onAeTextareaPaste(ai, $event)"
											(keydown)="onAeTextareaKeydown(ai, $event, $any($event.target))"
											#aeTA></textarea>
										<div class="ae-input-actions">
											<button type="button" class="ae-parse-btn" (click)="importAeFromTextarea(ai, aeTA.value)">
												<i class="pi pi-check"></i> Parse
											</button>
											<button type="button" class="ae-cancel-btn" (click)="closeAeInput()">
												<i class="pi pi-times"></i>
											</button>
											<span class="ae-hint">Ctrl+Enter · Esc</span>
										</div>
										@if (aeImportStatus()?.ai === ai && aeImportStatus()?.type === 'error') {
											<span class="ae-status ae-status--error">
												<i class="pi pi-exclamation-triangle"></i>
												{{ aeImportStatus()!.message }}
											</span>
										}
									</div>
								</div>
							</div>
						}
						<!-- Static X/Y -->
						@if (getActionMode(action) === 'static') {
							<div class="form-row">
								<div class="form-field">
									<label class="form-label">X</label>
									<p-inputnumber [ngModel]="action.coords?.x ?? 0" (ngModelChange)="setActionCoord(ai, 'x', $event)"
										styleClass="p-inputnumber-fluid" [showButtons]="false" [minFractionDigits]="0" [maxFractionDigits]="2" />
								</div>
								<div class="form-field">
									<label class="form-label">Y</label>
									<p-inputnumber [ngModel]="action.coords?.y ?? 0" (ngModelChange)="setActionCoord(ai, 'y', $event)"
										styleClass="p-inputnumber-fluid" [showButtons]="false" [minFractionDigits]="0" [maxFractionDigits]="2" />
								</div>
							</div>
						}
						<!-- Width / Height -->
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Width</label>
								<p-inputnumber [ngModel]="action.size?.width ?? 0" (ngModelChange)="setActionSize(ai, 'width', $event)"
									styleClass="p-inputnumber-fluid" [showButtons]="false" />
							</div>
							<div class="form-field">
								<label class="form-label">Height</label>
								<p-inputnumber [ngModel]="action.size?.height ?? 0" (ngModelChange)="setActionSize(ai, 'height', $event)"
									styleClass="p-inputnumber-fluid" [showButtons]="false" />
							</div>
						</div>
						<!-- Source image -->
						<div class="form-row">
							<div class="form-field flex-1">
								<label class="form-label">Source Image</label>
								<div class="picker-row">
									<input pInputText class="flex-1 text-xs" readonly
										[value]="fileName(action.source?.src)"
										[title]="action.source?.src ?? ''"
										placeholder="(no image)" />
									<button class="picker-btn" type="button" (click)="openActionSourcePicker(ai)" title="Browse files">
										<i class="pi pi-folder-open text-xs"></i>
									</button>
									@if (action.source?.src) {
										<button class="picker-btn" type="button" (click)="clearActionSource(ai)" title="Clear image">
											<i class="pi pi-times text-xs"></i>
										</button>
									}
								</div>
							</div>
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'action-use-bg-' + ai" [binary]="true"
									[ngModel]="action.source?.useBackgroundImage ?? false"
									(ngModelChange)="setActionSourceField(ai, 'useBackgroundImage', $event)" />
								<label class="form-label" [attr.for]="'action-use-bg-' + ai">Use Background</label>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Start Frame</label>
								<p-inputnumber [ngModel]="action.startFrame ?? null" (ngModelChange)="setActionField(ai, 'startFrame', $event)"
									styleClass="p-inputnumber-fluid" [showButtons]="false" />
							</div>
							<div class="form-field">
								<label class="form-label">End Frame</label>
								<p-inputnumber [ngModel]="action.endFrame ?? null" (ngModelChange)="setActionField(ai, 'endFrame', $event)"
									styleClass="p-inputnumber-fluid" [showButtons]="false" />
							</div>
							<div class="form-field">
								<label class="form-label">Start Scene</label>
								<p-select [options]="sceneSlugOptions(action.startScene)" optionValue="value" appendTo="body" [showClear]="true"
									placeholder="(none)" [ngModel]="action.startScene ?? null"
									(ngModelChange)="setActionField(ai, 'startScene', $event || undefined)"
									styleClass="p-select-fluid" />
							</div>
							<div class="form-field">
								<label class="form-label">End Scene</label>
								<p-select [options]="sceneSlugOptions(action.endScene)" optionValue="value" appendTo="body" [showClear]="true"
									placeholder="(none)" [ngModel]="action.endScene ?? null"
									(ngModelChange)="setActionField(ai, 'endScene', $event || undefined)"
									styleClass="p-select-fluid" />
							</div>
						</div>
						<!-- Data fields (type-dependent) -->
						@if (action.type === 'LINK' || action.type === 'SHARE') {
							<div class="form-row">
								<div class="form-field flex-1">
									<label class="form-label">Href / URL</label>
									<input pInputText class="w-full text-xs" [value]="action.data?.href ?? ''"
										(change)="setActionDataField(ai, 'href', $any($event.target).value)" />
								</div>
							</div>
						}
						@if (action.type === 'SEEK') {
							<div class="form-row">
								<div class="form-field">
									<label class="form-label">Target Chapter</label>
									<p-select [options]="chapterOptions(action.data?.chapter)" optionValue="value" appendTo="body"
										[ngModel]="action.data?.chapter ?? ''" (ngModelChange)="setSeekChapter(ai, $event)"
										styleClass="p-select-fluid" />
								</div>
								<div class="form-field">
									<label class="form-label">Target Scene</label>
									<p-select [options]="sceneOptionsFor(action.data?.chapter, action.data?.scene)" optionValue="value" appendTo="body"
										[showClear]="true" placeholder="(none)" [ngModel]="action.data?.scene ?? null"
										(ngModelChange)="setActionDataField(ai, 'scene', $event)"
										styleClass="p-select-fluid" />
								</div>
							</div>
						}
						<div class="form-row">
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'action-masked-check-' + ai" [binary]="true" [ngModel]="action.masked ?? false"
									(ngModelChange)="setActionField(ai, 'masked', $event)" />
								<label class="form-label" [attr.for]="'action-masked-check-' + ai">Masked</label>
							</div>
							<div class="form-field form-field--check">
								<p-checkbox [inputId]="'action-disabled-mobile-check-' + ai" [binary]="true" [ngModel]="action.disabledOnMobile ?? false"
									(ngModelChange)="setActionField(ai, 'disabledOnMobile', $event)" />
								<label class="form-label" [attr.for]="'action-disabled-mobile-check-' + ai">Disabled on Mobile</label>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field" style="flex-basis: 100%">
								<label class="form-label">Attributes</label>
								<app-attributes-editor
									[data]="action.attributes ?? null"
									(dataChange)="setActionAttributes(ai, $event)"
								/>
							</div>
						</div>
					</div>
				}
			</div>
		}
</div>

<!-- Effects -->
<div class="flex flex-col gap-1.5">
	<div class="flex items-center justify-between gap-3 px-3 py-2 bg-wb-surface rounded-wb-sm border border-wb-border">
		<span class="text-[13px] font-semibold text-wb-text flex items-center gap-1.5">
			Effects
			@if ((data().effects ?? []).length > 0) {
				<span class="wb-count-badge">{{ (data().effects ?? []).length }}</span>
			}
		</span>
		<div class="flex gap-1.5 items-center">
			<p-select
				[options]="effectMethodOptions"
				optionValue="value"
				appendTo="body"
				[ngModel]="newEffectMethod()"
				(ngModelChange)="newEffectMethod.set($event)"
			/>
			<button pButton size="small" (click)="addEffect()"><i class="pi pi-plus"></i>Add</button>
		</div>
	</div>
	@if ((data().effects ?? []).length === 0) {
		<div class="text-center text-wb-muted text-[12px] py-4 px-3 bg-wb-bg border border-dashed border-wb-border rounded-wb-sm">No effects.</div>
	}
	@for (effect of data().effects; track $index; let ei = $index) {
		<div class="action-card">
			<div class="action-header" (click)="toggleEffect(ei)">
				<button
					pButton
					[text]="true"
					size="small"
					severity="secondary"
					class="action-chevron-btn"
					(click)="$event.stopPropagation(); toggleEffect(ei)"
				>
					<i class="pi" [class.pi-chevron-down]="effectExpanded().has(ei)" [class.pi-chevron-right]="!effectExpanded().has(ei)"></i>
				</button>
				<span class="action-badge action-badge--{{ effect.data?.method ?? 'SET' }}">{{ effect.data?.method ?? '?' }}</span>
				<span class="text-[12px] text-wb-text truncate flex-1">{{ effect.name || '(unnamed)' }}</span>
				<span class="action-actions" (click)="$event.stopPropagation()">
					<button pButton [text]="true" size="small" severity="secondary" [disabled]="ei === 0" (click)="moveEffect(ei, -1)"><i class="pi pi-chevron-up"></i></button>
					<button pButton [text]="true" size="small" severity="secondary" [disabled]="ei === (data().effects?.length ?? 0) - 1" (click)="moveEffect(ei, 1)"><i class="pi pi-chevron-down"></i></button>
					<button pButton [text]="true" size="small" severity="danger" (click)="removeEffect(ei)"><i class="pi pi-trash"></i></button>
				</span>
			</div>
			@if (effectExpanded().has(ei)) {
				<div class="action-body">
					<div class="form-row">
						<div class="form-field">
							<label class="form-label">Trigger</label>
							<p-select [options]="effectTriggerOptions" optionValue="value" appendTo="body"
								[ngModel]="effect.trigger ?? 'BEFORE_SCENE_START'" (ngModelChange)="setEffectField(ei, 'trigger', $event)"
								styleClass="p-select-fluid" />
						</div>
						<div class="form-field">
							<label class="form-label">Method</label>
							<p-select [options]="effectMethodOptions" optionValue="value" appendTo="body"
								[ngModel]="effect.data?.method ?? ''" (ngModelChange)="setEffectMethod(ei, $event)"
								styleClass="p-select-fluid" />
						</div>
						<div class="form-field">
							<label class="form-label">Name</label>
							<input pInputText class="w-full text-xs" [value]="effect.name ?? ''"
								(change)="setEffectField(ei, 'name', $any($event.target).value)" />
						</div>
					</div>
					@if (effect.trigger === 'BEFORE_SCENE_FRAME') {
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Frame Index</label>
								<p-inputnumber [ngModel]="effect.frameIndex ?? 0" (ngModelChange)="setEffectField(ei, 'frameIndex', $event)"
									styleClass="p-inputnumber-fluid" [showButtons]="false" />
							</div>
						</div>
					}

					<!-- Method-specific fields -->
					@if (effect.data?.method === 'SET' || effect.data?.method === 'SUBTRACT') {
						<div class="form-row">
							<div class="form-field flex-1">
								<label class="form-label">Variable</label>
								<app-var-picker [value]="effect.data?.input ?? null" (valueChange)="setEffectDataField(ei, 'input', $event)" />
							</div>
						</div>
						<div class="form-row">
							<div class="form-field" style="flex-basis: 100%">
								<label class="form-label">Attributes</label>
								<app-attributes-editor
									[data]="effect.data?.attributes ?? null"
									(dataChange)="setEffectDataField(ei, 'attributes', $event)"
								/>
							</div>
						</div>
					}
					@if (effect.data?.method === 'WAIT') {
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Target Chapter</label>
								<p-select [options]="chapterOptions(effect.data?.chapter)" optionValue="value" appendTo="body"
									[ngModel]="effect.data?.chapter ?? ''" (ngModelChange)="setEffectChapter(ei, $event)"
									styleClass="p-select-fluid" />
							</div>
							<div class="form-field">
								<label class="form-label">Target Scene</label>
								<p-select [options]="sceneOptionsFor(effect.data?.chapter, effect.data?.scene)" optionValue="value" appendTo="body"
									[showClear]="true" placeholder="(none)" [ngModel]="effect.data?.scene ?? null"
									(ngModelChange)="setEffectDataField(ei, 'scene', $event)"
									styleClass="p-select-fluid" />
							</div>
						</div>
					}
					@if (effect.data?.method === 'EVALUATE') {
						<div class="form-row">
							<div class="form-field flex-1">
								<label class="form-label">Score Variable</label>
								<app-var-picker [value]="effect.data?.input ?? null" (valueChange)="setEffectDataField(ei, 'input', $event)" />
							</div>
						</div>
						<div class="form-row">
							<div class="form-field">
								<label class="form-label">Min (success threshold)</label>
								<p-inputnumber [ngModel]="effect.data?.output?.condition?.min ?? 0" (ngModelChange)="setEffectConditionField(ei, 'min', $event)"
									styleClass="p-inputnumber-fluid" [showButtons]="false" />
							</div>
							<div class="form-field">
								<label class="form-label">Draw threshold</label>
								<p-inputnumber [ngModel]="effect.data?.output?.condition?.draw ?? null" (ngModelChange)="setEffectConditionField(ei, 'draw', $event)"
									styleClass="p-inputnumber-fluid" [showButtons]="false" placeholder="(none)" />
							</div>
						</div>
						<div class="form-row">
							<div class="form-field" style="flex-basis: 100%">
								<label class="form-label">Success — Attributes</label>
								<app-attributes-editor
									[data]="effect.data?.output?.success?.attributes ?? null"
									(dataChange)="setEffectOutputAttributes(ei, 'success', $event)"
								/>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field" style="flex-basis: 100%">
								<label class="form-label">Fail — Attributes</label>
								<app-attributes-editor
									[data]="effect.data?.output?.fail?.attributes ?? null"
									(dataChange)="setEffectOutputAttributes(ei, 'fail', $event)"
								/>
							</div>
						</div>
						<div class="form-row">
							<div class="form-field" style="flex-basis: 100%">
								<label class="form-label">Draw — Attributes</label>
								<app-attributes-editor
									[data]="effect.data?.output?.draw?.attributes ?? null"
									(dataChange)="setEffectOutputAttributes(ei, 'draw', $event)"
								/>
							</div>
						</div>
					}
				</div>
			}
		</div>
	}
</div>

<app-media-picker
	[(visible)]="pickerVisible"
	[categories]="pickerCategories()"
	[currentValue]="data()?.background?.source?.src ?? ''"
	(fileSelected)="setBgSrc($event)"
/>

<app-media-picker
	[(visible)]="maskPickerVisible"
	[categories]="maskPickerCategories"
	[currentValue]="data()?.background?.source?.maskSrc ?? ''"
	(fileSelected)="setMaskSrc($event)"
/>

<app-media-picker
	[(visible)]="actionSourcePickerVisible"
	[categories]="['images']"
	currentValue=""
	(fileSelected)="setActionSourceSrc($event)"
/>
